import { pluralizeStepType } from "shared/lib/utils"

import { CreditsBalance } from "shared/utils/credits"

import { CreditCosts, DIYComposerState, PublicTQAShape, Service, StepType, TQAType } from "./interfaces"

export type StateWithCredits = DIYComposerState & { creditsBalance: CreditsBalance; creditCosts: CreditCosts }

export const creditsLoaded = (state: DIYComposerState): state is StateWithCredits =>
  !!state.creditsBalance && !!state.creditCosts

export const remainingPercentage = ({ remaining, monthly_total }: CreditsBalance): number =>
  monthly_total > 0 ? Math.min(100, Math.max(0, Math.round((remaining / monthly_total) * 100))) : 0

// A step's cost lives on the catalog entry rather than on the step the composer stores, because
// only the catalog is loaded on every path into the builder (fresh pick, edit, template, chat).
export const stepCredits = (services: Array<Service>, step: TQAType | undefined, type: StepType): number => {
  if (!step) return 0

  const service = services.find(s => s.id === (step.service?.id ?? step.channel?.id))
  const catalogStep = service?.[`public_${pluralizeStepType(type)}`]?.find(
    (tqa: PublicTQAShape) => tqa.full_module_name === step.full_module_name,
  )

  return catalogStep?.credits ?? step.credits ?? 0
}

export const appletRunCost = (state: StateWithCredits): number => {
  const { services, creditCosts } = state

  return (
    stepCredits(services, state.selectedTrigger, "trigger") +
    state.selectedQueries.reduce((sum, query) => sum + stepCredits(services, query, "query"), 0) +
    state.selectedActions.reduce((sum, action) => sum + stepCredits(services, action, "action"), 0) +
    (state.filterCode ? creditCosts.filter_code : 0) +
    (state.delay > 0 ? creditCosts.delay : 0) +
    // AI Action Assist has no canvas step or badge, so this is the only place its cost is counted.
    (state.smartFillConfigured ? creditCosts.ai_action_assist : 0)
  )
}

export const creditLabel = (credits: number): string => `${credits} ${credits === 1 ? "credit" : "credits"}`
